import React, { ReactNode, useState } from 'react';
import {
	FieldErrors,
	UseFormHandleSubmit,
	UseFormRegister,
	UseFormWatch,
} from 'react-hook-form';
import { UseMutateFunction } from '@tanstack/react-query';

import {
	Button,
	DynamicIconWrapper,
	Icons,
	Modal,
	TextArea,
	TextClamp,
	Tooltip } from '@components/common';
import FilesList from '@components/common/FilesList';
import AttachDocuments from '@components/ModalComponents/AssetForm/Attributes/Status/AttachAssetDocuments';

import { isEqual } from '@utils/common';
import { getFileIds } from '@utils/formatter';

import { IAssetFile } from '@interfaces/assets/asset';
import {
	IDefaultValuesAttribute,
	TRadioDefaultValueStatuses,
} from '@interfaces/components/form';

import { MODAL_WIDTH_580 } from '@constants/modals';

import styles from './AttributeCard.module.scss';

interface IAttributeCardProps {
	changeEditMode: React.Dispatch<React.SetStateAction<boolean>>;
	detailsName: string;
	editHandler: UseMutateFunction<any>;
	errors: FieldErrors;
	handleSubmit: UseFormHandleSubmit<IDefaultValuesAttribute>;
	id?: string;
	isEditAccess: boolean;
	isEditMode: boolean;
	register: UseFormRegister<IDefaultValuesAttribute>;
	statusName: string;
	title: string;
	watch: UseFormWatch<IDefaultValuesAttribute>;
	icon: React.ReactNode;
	children: ReactNode;
	radioValue: TRadioDefaultValueStatuses;
	fileFieldName: string;
	changeFiles: (fileFieldName: string, filesIds: Array<string>) => void;
}

const AttributeCard = ({
	changeEditMode,
	detailsName,
	editHandler,
	errors,
	handleSubmit,
	id,
	isEditAccess,
	isEditMode,
	register,
	title,
	watch,
	icon,
	children,
	radioValue,
	fileFieldName,
	changeFiles,
}: IAttributeCardProps) => {
	const [isOpenUploadFilesModal, setIsOpenUploadFilesModal] = useState(false);
	const detailsValue = watch(detailsName as any);
	const files = watch(fileFieldName as any);

	const activateEditMode = () => changeEditMode(true);

	const handleRemoveFile = (fileId: string) =>
		changeFiles(fileFieldName, files.filter((file: IAssetFile) => file.id !== fileId));

	const toggleUploadFilesModal = () => setIsOpenUploadFilesModal(prevState => !prevState);

	const handleUploadFiles = (uploadedFiles: Array<IAssetFile>) =>
		changeFiles(fileFieldName, [...files, ...uploadedFiles]);

	const onSubmit = () => {
		editHandler({
			id,
			details: detailsValue || null,
			status: radioValue,
			filesIds: getFileIds(files),
		} as any);

		changeEditMode(false);
	};

	return (
		<div className={styles.attributeCardContainer}>
			<Modal
				isOpen={isOpenUploadFilesModal}
				isScrollableContent
				onClose={toggleUploadFilesModal}
				title='Upload file'
				width={MODAL_WIDTH_580}
			>
				<AttachDocuments
					handleUploadFiles={handleUploadFiles}
					toggleUploadFilesModal={toggleUploadFilesModal}
				/>
			</Modal>

			<div className={styles.title}>
				<span>
					<span className={styles.bold}>{title}:&nbsp;</span>
					{icon}
				</span>
				{!isEditMode && isEditAccess
					&& <span onClick={activateEditMode}>
						<DynamicIconWrapper icon={Icons.editIcon} />
					</span>
				}
			</div>
			{isEditMode && children}
			{isEditMode
				? <TextArea
					className={styles.details}
					errorMessage={errors[detailsName] && errors[detailsName]?.message as string}
					label='Details'
					name={detailsName}
					placeholder='Enter description of the details'
					register={register as any}
					rows={11}
					withUseForms
				/>
				: (detailsValue
					&& <>
						<div className={styles.subTitle}>Details</div>
						<div className={styles.details}>
							<TextClamp text={detailsValue} />
						</div>
					</>
				)
			}
			{(!isEditMode && !!files.length)
				&& <div>
					<div className={styles.subTitle}>Attached files</div>
					<FilesList
						files={files}
						isViewMode
						maxWidth={180}
					/>
				</div>
			}
			{isEditMode
				&& <>
					<div className={styles.filesContainer}>
						<div className={styles.fileButton}>
							<Tooltip
								content={
									<>
										Acceptable size: 10MB
										<br />
										Acceptable extensions: doc, docx, xls, xlsx, txt, ppt, pptx, jpg, png, pdf
									</>
								}
								maxWidth={428}
								placement='top-start'
							>
								<div>
									<Button
										icon={Icons.plus()}
										onClick={toggleUploadFilesModal}
										text='Add attachment'
										withoutBackground
									/>
								</div>
							</Tooltip>
						</div>
						<FilesList
							files={files}
							handleRemoveFile={handleRemoveFile}
							maxWidth={180}
						/>
					</div>
					<div className={styles.submitButton}>
						<Button
							onClick={handleSubmit(onSubmit)}
							text='Save'
						/>
					</div>
				</>
			}
		</div>
	);
};

export default React.memo(AttributeCard, (prevProps, nextProps) => isEqual(prevProps, nextProps));
